import { buildApiUrl } from "@/lib/api/config"

/** OAuth token endpoint + ABP RemoteServiceErrorResponse */
const pickErrorMessage = (data: unknown): string | null => {
  if (typeof data !== "object" || data === null) {
    return null
  }
  const o = data as Record<string, unknown>
  if (typeof o.error_description === "string" && o.error_description) {
    return o.error_description
  }

  const err = o.error
  /** Сначала разбираем объект `error`: при 500 ABP часто кладёт общий текст в `message`, а стек/причину — в `details`. */
  if (typeof err === "object" && err !== null) {
    const e = err as Record<string, unknown>
    const msg = typeof e.message === "string" ? e.message : ""
    const detRaw = typeof e.details === "string" ? e.details.trim() : ""
    if (msg && detRaw && detRaw !== msg) {
      const short = detRaw.length > 400 ? `${detRaw.slice(0, 400)}…` : detRaw
      return `${msg} — ${short}`
    }
    if (msg) return msg
    if (detRaw) return detRaw
    const ve = e.validationErrors
    if (Array.isArray(ve) && ve.length > 0) {
      const first = ve[0] as Record<string, unknown>
      if (typeof first.message === "string" && first.message) {
        return first.message
      }
    }
  }
  if (typeof err === "string" && err) {
    return err
  }

  if (typeof o.message === "string" && o.message) {
    return o.message
  }
  return null
}

export class ApiError extends Error {
  status: number
  body: unknown

  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.body = body
  }
}

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError && error.message) {
    return error.message
  }
  return fallback
}

type FetchJsonOptions = {
  method?: string
  body?: unknown
  token?: string | null
  headers?: Record<string, string>
}

export const fetchJson = async <T>(path: string, options: FetchJsonOptions = {}): Promise<T> => {
  const { method = "GET", body, token, headers = {} } = options
  const h: Record<string, string> = { ...headers }
  if (body !== undefined && !(body instanceof FormData)) {
    h["Content-Type"] = "application/json"
  }
  if (token) {
    h.Authorization = `Bearer ${token}`
  }
  const requestUrl = buildApiUrl(path)
  const res = await fetch(requestUrl, {
    method,
    headers: h,
    /** Иначе после DELETE следующий GET списка может прийти из HTTP-кэша — запись «не удаляется» */
    cache: "no-store",
    /**
     * Без этого на dev (Vite proxy, same-origin `/api`) браузер шлёт cookies с localhost.
     * Если бэкенд выставил antiforgery-cookie, а SPA не шлёт X-XSRF-TOKEN — ABP отвечает 400 на DELETE/PUT/POST.
     * Авторизация у нас только через Bearer в заголовке, cookies API не нужны.
     */
    credentials: "omit",
    ...(body !== undefined
      ? { body: body instanceof FormData ? body : JSON.stringify(body) }
      : {}),
  })
  const text = await res.text()
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text) as unknown
    } catch {
      data = text
    }
  }
  if (!res.ok) {
    const msg = pickErrorMessage(data) ?? res.statusText
    throw new ApiError(msg || "Request failed", res.status, data)
  }
  return data as T
}

type FetchTextOptions = {
  method?: string
  token?: string | null
  headers?: Record<string, string>
}

/** GET/POST ответ как сырая строка (без JSON.parse на успехе) — для тел логов и т. п. */
export const fetchText = async (path: string, options: FetchTextOptions = {}): Promise<string> => {
  const { method = "GET", token, headers = {} } = options
  const h: Record<string, string> = { ...headers }
  if (token) {
    h.Authorization = `Bearer ${token}`
  }
  const res = await fetch(buildApiUrl(path), {
    method,
    headers: h,
    cache: "no-store",
    credentials: "omit",
  })
  const text = await res.text()
  let data: unknown = text || null
  if (text) {
    try {
      data = JSON.parse(text) as unknown
    } catch {
      data = text
    }
  }
  if (!res.ok) {
    const msg = pickErrorMessage(data) ?? res.statusText
    throw new ApiError(msg || "Request failed", res.status, data)
  }
  return typeof data === "string" ? data : text
}

export type TokenResponse = {
  access_token: string
  token_type?: string
  expires_in?: number
  refresh_token?: string
}

export const postFormUrlEncoded = async <T>(path: string, params: Record<string, string>): Promise<T> => {
  const body = new URLSearchParams(params)
  const res = await fetch(buildApiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
    cache: "no-store",
    credentials: "omit",
  })
  const text = await res.text()
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text) as unknown
    } catch {
      data = text
    }
  }
  if (!res.ok) {
    const msg = pickErrorMessage(data) ?? res.statusText
    throw new ApiError(msg || "Auth failed", res.status, data)
  }
  return data as T
}
